/**
 * templateSchema — pure JSON-schema validator for BuildingTemplate.
 *
 * Mirrors the JSON schema in the requirements' Data Model section.
 * Returns a structurally-valid `BuildingTemplate` on success or `null` on
 * any structural failure. Does NOT raise; any invalid input yields `null`.
 *
 * This is the shared parser used by:
 *   - The editor's API client (to filter invalid list items, P8).
 *   - The runtime `TemplateRegistry` (to drop invalid templates, Req 16.2).
 *   - Task 5's wire-format definitions.
 *
 * Pure. No React, no DOM, no side effects.
 */

import type {
  BannerStripDirection,
  BannerStripSlot,
  BuildingTemplate,
  BuildingTemplateSlots,
  FootprintBaseline,
  ImageRecord,
  LineSlot,
  Point,
  PointSlot,
  ProjectTypeHint,
  PublishedImageRecord,
  PublishedImageSource,
  Rect,
  RectSlot,
} from "./types";
import { PROJECT_TYPE_HINTS } from "./types";

const NAME_PATTERN = /^[a-z][a-z0-9_-]{1,47}$/;
const SHA256_PATTERN = /^[0-9a-f]{64}$/;
const MAX_DESCRIPTION_LENGTH = 280;
const BANNER_STRIP_MAX_COUNT_MIN = 1;
const BANNER_STRIP_MAX_COUNT_MAX = 8;
const VALID_DIRECTIONS: readonly BannerStripDirection[] = ["row", "column"];
const VALID_SOURCES: readonly PublishedImageSource[] = [
  "original",
  "background_removed",
];

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 1;
}

function validatePoint(raw: unknown): Point | null {
  if (!isObject(raw)) return null;
  if (!isNonNegativeInteger(raw.x)) return null;
  if (!isNonNegativeInteger(raw.y)) return null;
  return { x: raw.x, y: raw.y };
}

function validateImageRecord(raw: unknown): ImageRecord | null {
  if (!isObject(raw)) return null;
  if (typeof raw.s3_key !== "string" || raw.s3_key.length === 0) return null;
  if (!isPositiveInteger(raw.width)) return null;
  if (!isPositiveInteger(raw.height)) return null;
  if (typeof raw.sha256 !== "string" || !SHA256_PATTERN.test(raw.sha256)) {
    return null;
  }
  return {
    s3_key: raw.s3_key,
    width: raw.width,
    height: raw.height,
    sha256: raw.sha256,
  };
}

function validateCrop(raw: unknown): Rect | null {
  if (!isObject(raw)) return null;
  if (!isNonNegativeInteger(raw.x)) return null;
  if (!isNonNegativeInteger(raw.y)) return null;
  if (!isPositiveInteger(raw.width)) return null;
  if (!isPositiveInteger(raw.height)) return null;
  return { x: raw.x, y: raw.y, width: raw.width, height: raw.height };
}

function validatePublishedImageRecord(
  raw: unknown,
): PublishedImageRecord | null {
  if (!isObject(raw)) return null;
  const base = validateImageRecord(raw);
  if (base === null) return null;

  if (typeof raw.source !== "string") return null;
  if (!VALID_SOURCES.includes(raw.source as PublishedImageSource)) {
    return null;
  }

  let crop: Rect | null = null;
  if (raw.crop !== undefined && raw.crop !== null) {
    const maybeCrop = validateCrop(raw.crop);
    if (maybeCrop === null) return null;
    crop = maybeCrop;
  }

  return {
    s3_key: base.s3_key,
    width: base.width,
    height: base.height,
    sha256: base.sha256,
    crop,
    source: raw.source as PublishedImageSource,
    // Optional (migration 178): carried only when it is a non-empty string; anything
    // else means "no WebP", never a reason to drop the template.
    ...(typeof raw.webp_s3_key === "string" && raw.webp_s3_key ? { webp_s3_key: raw.webp_s3_key } : {}),
  };
}

/**
 * A baseline end may sit off the canvas: art centred on its cell with a footprint wider than
 * the picture (the dive ladder, the half-sunk log, the stage and workbench stalls) starts left of
 * x = 0. Rejecting it dropped the whole row and the spot drew the placeholder (evals' dev shots,
 * 2026-10-24: 23 rows on dev). Slots and anchors still sit on the canvas.
 */
function validateBaselinePoint(raw: unknown): Point | null {
  if (!isObject(raw)) return null;
  if (!isInteger(raw.x) || !isInteger(raw.y)) return null;
  return { x: raw.x, y: raw.y };
}

function validateFootprintBaseline(raw: unknown): FootprintBaseline | null {
  if (!isObject(raw)) return null;
  const left = validateBaselinePoint(raw.left);
  const right = validateBaselinePoint(raw.right);
  if (left === null || right === null) return null;
  if (!(right.x > left.x)) return null;
  // Optional, and never a reason to drop the template: anything but 1..7 is one cell.
  const cells = raw.cells;
  return isPositiveInteger(cells) && cells <= 7 ? { left, right, cells } : { left, right };
}

function validatePointSlot(raw: unknown): PointSlot | null {
  if (!isObject(raw)) return null;
  if (raw.shape !== "point") return null;
  if (!isNonNegativeInteger(raw.x)) return null;
  if (!isNonNegativeInteger(raw.y)) return null;
  return { shape: "point", x: raw.x, y: raw.y };
}

function validateRectSlot(raw: unknown): RectSlot | null {
  if (!isObject(raw)) return null;
  if (raw.shape !== "rect") return null;
  if (!isInteger(raw.x)) return null;
  if (!isInteger(raw.y)) return null;
  if (!isPositiveInteger(raw.width)) return null;
  if (!isPositiveInteger(raw.height)) return null;
  return {
    shape: "rect",
    x: raw.x,
    y: raw.y,
    width: raw.width,
    height: raw.height,
    ...(typeof raw.face === "string" ? { face: raw.face } : {}),
  } as RectSlot;
}

function validateBannerStripSlot(raw: unknown): BannerStripSlot | null {
  if (!isObject(raw)) return null;
  if (raw.shape !== "banner_strip") return null;

  const anchor = validatePoint(raw.anchor);
  if (anchor === null) return null;

  if (typeof raw.direction !== "string") return null;
  if (!VALID_DIRECTIONS.includes(raw.direction as BannerStripDirection)) {
    return null;
  }

  if (!isObject(raw.banner_size)) return null;
  if (!isPositiveInteger(raw.banner_size.width)) return null;
  if (!isPositiveInteger(raw.banner_size.height)) return null;

  if (!isNonNegativeInteger(raw.spacing)) return null;

  if (
    !isPositiveInteger(raw.max_count) ||
    raw.max_count < BANNER_STRIP_MAX_COUNT_MIN ||
    raw.max_count > BANNER_STRIP_MAX_COUNT_MAX
  ) {
    return null;
  }

  return {
    shape: "banner_strip",
    anchor,
    direction: raw.direction as BannerStripDirection,
    banner_size: {
      width: raw.banner_size.width,
      height: raw.banner_size.height,
    },
    spacing: raw.spacing,
    max_count: raw.max_count,
    ...(typeof raw.face === "string" ? { face: raw.face } : {}),
  } as BannerStripSlot;
}

function validateSlots(raw: unknown): BuildingTemplateSlots | null {
  if (!isObject(raw)) return null;
  // A landmark or a ground tile has no door (2026-10-16): absent is valid, malformed is not.
  let door: PointSlot | null = null;
  if (raw.door !== undefined && raw.door !== null) {
    door = validatePointSlot(raw.door);
    if (door === null) return null;
  }

  // Sign and classification_banners are optional
  let sign: BannerStripSlot | null = null;
  if (raw.sign !== undefined && raw.sign !== null) {
    sign = validateBannerStripSlot(raw.sign);
    if (sign === null) return null;
  }

  let classification_banners: BannerStripSlot | null = null;
  if (raw.classification_banners !== undefined && raw.classification_banners !== null) {
    classification_banners = validateBannerStripSlot(raw.classification_banners);
    if (classification_banners === null) return null;
  }

  // Ornament and preview_window are optional (may be null or absent)
  let ornament: RectSlot | null = null;
  if (raw.ornament !== undefined && raw.ornament !== null) {
    const maybeOrnament = validateRectSlot(raw.ornament);
    if (maybeOrnament === null) return null;
    ornament = maybeOrnament;
  }

  let preview_window: RectSlot | null = null;
  if (raw.preview_window !== undefined && raw.preview_window !== null) {
    const maybePreview = validateRectSlot(raw.preview_window);
    if (maybePreview === null) return null;
    preview_window = maybePreview;
  }

  let door_line: LineSlot | null = null;
  if (raw.door_line !== undefined && raw.door_line !== null) {
    const dl = raw.door_line;
    if (isObject(dl) && isObject(dl.start) && isObject(dl.end)
      && typeof (dl.start as Record<string, unknown>).x === "number"
      && typeof (dl.start as Record<string, unknown>).y === "number"
      && typeof (dl.end as Record<string, unknown>).x === "number"
      && typeof (dl.end as Record<string, unknown>).y === "number") {
      door_line = {
        shape: "line",
        start: { x: (dl.start as Record<string, unknown>).x as number, y: (dl.start as Record<string, unknown>).y as number },
        end: { x: (dl.end as Record<string, unknown>).x as number, y: (dl.end as Record<string, unknown>).y as number },
      };
    }
  }

  return { door, door_line, ornament, preview_window, sign, classification_banners };
}

function validateProjectTypeHint(raw: unknown): ProjectTypeHint | null {
  if (typeof raw !== "string") return null;
  if (!PROJECT_TYPE_HINTS.includes(raw as ProjectTypeHint)) return null;
  return raw as ProjectTypeHint;
}

/**
 * Validate an arbitrary value as a `BuildingTemplate`. Returns the typed
 * template on success; returns `null` for any structural failure.
 */
export function validateTemplate(raw: unknown): BuildingTemplate | null {
  if (!isObject(raw)) return null;

  if (typeof raw.name !== "string" || !NAME_PATTERN.test(raw.name)) {
    return null;
  }
  if (!isPositiveInteger(raw.version)) return null;

  let projectTypeHint: ProjectTypeHint | undefined;
  if (raw.project_type_hint !== undefined && raw.project_type_hint !== null) {
    const hint = validateProjectTypeHint(raw.project_type_hint);
    if (hint === null) return null;
    projectTypeHint = hint;
  }

  let description: string | undefined;
  if (raw.description !== undefined && raw.description !== null) {
    if (typeof raw.description !== "string") return null;
    if (raw.description.length > MAX_DESCRIPTION_LENGTH) return null;
    description = raw.description;
  }

  const originalImage = validateImageRecord(raw.original_image);
  if (originalImage === null) return null;

  let backgroundRemovedImage: ImageRecord | null | undefined;
  if (
    raw.background_removed_image !== undefined &&
    raw.background_removed_image !== null
  ) {
    const maybe = validateImageRecord(raw.background_removed_image);
    if (maybe === null) return null;
    backgroundRemovedImage = maybe;
  } else if (raw.background_removed_image === null) {
    backgroundRemovedImage = null;
  }

  const publishedImage = validatePublishedImageRecord(raw.published_image);
  if (publishedImage === null) return null;

  let publishedAt: string | null | undefined;
  if (raw.published_at !== undefined) {
    if (raw.published_at === null) {
      publishedAt = null;
    } else if (typeof raw.published_at === "string") {
      publishedAt = raw.published_at;
    } else {
      return null;
    }
  }

  const footprintBaseline = validateFootprintBaseline(raw.footprint_baseline);
  if (footprintBaseline === null) return null;

  const slots = validateSlots(raw.slots);
  if (slots === null) return null;

  const template: Record<string, unknown> = {
    id: (raw as { id?: string }).id,
    name: raw.name,
    version: raw.version,
    original_image: originalImage,
    published_image: publishedImage,
    footprint_baseline: footprintBaseline,
    slots,
  };
  if (projectTypeHint !== undefined) {
    template.project_type_hint = projectTypeHint;
  }
  if (description !== undefined) {
    template.description = description;
  }
  if (backgroundRemovedImage !== undefined) {
    template.background_removed_image = backgroundRemovedImage;
  }
  if (publishedAt !== undefined) {
    template.published_at = publishedAt;
  }
  if (typeof raw.building_kind === "string") {
    template.building_kind = raw.building_kind;
  }
  if (typeof raw.entrance_side === "string") {
    template.entrance_side = raw.entrance_side;
  }
  if (typeof raw.ground_variant === "string") {
    template.ground_variant = raw.ground_variant;
  }
  if (typeof raw.weight === "number" && raw.weight >= 1) {
    template.weight = raw.weight;
  }
  if (typeof raw.zone === "string" && raw.zone.length > 0) {
    template.zone = raw.zone;
  }
  if (typeof raw.walkable === "boolean") {
    template.walkable = raw.walkable;
  }
  if (typeof raw.walkable_occupant === "boolean") {
    template.walkable_occupant = raw.walkable_occupant;
  }
  if (typeof raw.system_ref === "string") {
    template.system_ref = raw.system_ref;
  }
  return template as unknown as BuildingTemplate;
}

export { NAME_PATTERN, SHA256_PATTERN, MAX_DESCRIPTION_LENGTH };
